import { MapMusicControl, MapTileSpec } from 'eolib';
import { worldResource } from '@/world';
import { Howl, Howler, type PannerAttributes } from 'howler';
import { Sequencer, WorkletSynthesizer } from 'spessasynth_lib';
import processorUrl from 'spessasynth_lib/dist/spessasynth_processor.min.js?url';
import type { Client } from '@/client';
import { GameState } from '@/game-state';
import { getInstrumentSfxPrefix, type SfxId } from '@/sfx';
import { padWithZeros } from '@/utils';
import type { Vector2 } from '@/vector';
import { originalMapTrack, originalMenuTrack, originalMusicTrack,refreshOriginalSoundtrack,subscribeOriginalSoundtrack } from '@/original-soundtrack';
import { CrossfadeMusic } from '@/crossfade-music';

const SPATIAL_PANNER: PannerAttributes = {
  panningModel: 'HRTF',
  distanceModel: 'inverse',
  refDistance: 1,
  maxDistance: 50,
  rolloffFactor: 1,
  coneInnerAngle: 360,
  coneOuterAngle: 360,
  coneOuterGain: 0,
};

export class AudioController {
  private client: Client;
  private sfxCache = new Map<string, Howl>();
  private spatialCache = new Map<string, Howl>();
  private ambientHowl: Howl | null = null;
  // A stale WebAudio graph is a recoverable sound problem, never a map-load error.
  private spatialAudioDisabled = false;
  private warnedSpatialAudioFailure = false;

  // MIDI state
  private midiContext: AudioContext | null = null;
  private midiSynth: WorkletSynthesizer | null = null;
  private midiSequencer: Sequencer | null = null;
  private sfBuffer: ArrayBuffer | null = null;
  private synthInitPromise: Promise<void> | null = null;
  private currentMfxId: number | null = null;
  private pendingMusic: { mfxId: number; loop: boolean } | null = null;

  // Deferred until first user gesture (browser autoplay policy)
  private gestureUnlocked = false;
  private queuedMusic: { mfxId: number; loop: boolean } | null = null;

  private alwaysLoop = true;
  private mapMusicState: { mfxId: number; loop: boolean } | null = null;
  private jukeboxActive = false;
  private originalMusic = new CrossfadeMusic(5, () => {
    if (this.client.state === GameState.InGame && this.client.map) {
      void this.playOriginal(originalMapTrack(this.client.mapId, this.client.map.name), true);
    }
  });
  private get originalAudio(): HTMLAudioElement | null { return this.originalMusic.current; }
  private originalTrack: string | null = null;
  private musicRequest = 0;

  constructor(client: Client) {
    this.client = client;
    subscribeOriginalSoundtrack(()=>{
      if(client.state===GameState.InGame&&client.map){void this.playOriginal(originalMapTrack(client.mapId,client.map.name),true);}
    });
    void refreshOriginalSoundtrack();

    client.configController.subscribe('masterVolume', () =>
      this.applyVolumeConfig(),
    );
    client.configController.subscribe('ambientVolume', () =>
      this.applyVolumeConfig(),
    );
    client.configController.subscribe('effectVolume', () =>
      this.applyVolumeConfig(),
    );
    client.configController.subscribe('musicVolume', () =>
      this.applyVolumeConfig(),
    );
    client.configController.subscribe('forceMusicLoop', () =>
      this.applyVolumeConfig(),
    );
    this.applyVolumeConfig();

    // Queue title music; also try immediate autoplay (works on refresh if
    // the browser remembers prior permission).
    this.handleStateChange(client.state);
    this.tryAutoplay();
  }

  /**
   * Attempt to create an AudioContext immediately on startup.
   * If the browser grants autoplay (context state = 'running'), unlock right
   * away. Otherwise we leave the context in place and the first user gesture
   * via notifyGesture() will resume it.
   * Wrapped in try-catch because iOS Safari may throw outside a gesture.
   */
  private tryAutoplay(): void {
    try {
      this.midiContext = new AudioContext();
      if (this.midiContext.state === 'running') {
        this.gestureUnlocked = true;
        if (this.queuedMusic) {
          const { mfxId, loop } = this.queuedMusic;
          this.queuedMusic = null;
          this.playMusic(mfxId, loop);
        }
      }
    } catch {
      // Autoplay blocked; will retry on first gesture via notifyGesture().
    }
  }

  private sfxUrl(id: SfxId): string {
    return worldResource(`/sfx/sfx${padWithZeros(id, 3)}.wav`);
  }

  private disableSpatialAudio(error: unknown): void {
    const message = error instanceof Error ? error.message : String(error);
    if (!/different audio ?context|audio.?node.*context|invalidaccesserror/i.test(message)) {
      return;
    }

    this.spatialAudioDisabled = true;
    if (!this.warnedSpatialAudioFailure) {
      this.warnedSpatialAudioFailure = true;
      console.warn('Spatial audio disabled after an AudioContext mismatch; continuing without spatial sound.', error);
    }
  }

  private getSpatialHowl(key: string, src: string): Howl | null {
    if (this.spatialAudioDisabled) return null;
    try {
      let howl = this.spatialCache.get(key);
      if (!howl) {
        howl = new Howl({ src: [src] });
        howl.pannerAttr(SPATIAL_PANNER);
        this.spatialCache.set(key, howl);
      }
      return howl;
    } catch (error) {
      this.disableSpatialAudio(error);
      console.warn('Spatial sound setup failed; using non-spatial audio.', error);
      return null;
    }
  }

  private applyVolumeConfig(): void {
    const cfg = this.client.configController;
    Howler.volume(cfg.masterVolume);
    if (this.ambientHowl) {
      this.ambientHowl.volume(cfg.ambientVolume);
    }
    this.alwaysLoop = cfg.forceMusicLoop;
    this.applyMusicVolume();
  }

  private applyMusicVolume(): void {
    const musicCfg = this.client.configController;
    this.originalMusic.setVolume(musicCfg.masterVolume * musicCfg.musicVolume);
    if (this.midiSynth) {
      const cfg = this.client.configController;
      this.midiSynth.setSystemParameter(
        'gain',
        cfg.masterVolume * cfg.musicVolume,
      );
    }
  }

  private getNearestAmbientSource(playerAt: Vector2): Vector2 | null {
    let nearest: Vector2 | null = null;
    let nearestDist = Number.POSITIVE_INFINITY;

    for (const row of this.client.map!.tileSpecRows) {
      for (const tile of row.tiles) {
        if (tile.tileSpec === MapTileSpec.AmbientSource) {
          const coords = { x: tile.x, y: row.y };
          const dist =
            Math.abs(coords.x - playerAt.x) + Math.abs(coords.y - playerAt.y);
          if (dist < nearestDist) {
            nearestDist = dist;
            nearest = coords;
          }
        }
      }
    }

    return nearest;
  }

  /** Play a bard note SFX by instrument and note ID */
  playNoteSfx(instrumentId: number, noteId: number): void {
    const prefix = getInstrumentSfxPrefix(instrumentId);
    if (!prefix) return;

    const key = `${prefix}${noteId}`;
    try {
      let howl = this.sfxCache.get(key);
      if (!howl) {
        howl = new Howl({
          src: [worldResource(`/sfx/${prefix}${padWithZeros(noteId, 3)}.wav`)],
        });
        this.sfxCache.set(key, howl);
      }
      const soundId = howl.play();
      howl.volume(this.client.configController.effectVolume, soundId);
    } catch (error) {
      this.disableSpatialAudio(error);
      console.warn('Sound effect unavailable; continuing without audio.', error);
    }
  }

  playNoteSfxAtPosition(
    instrumentId: number,
    noteId: number,
    coords: Vector2,
  ): void {
    const prefix = getInstrumentSfxPrefix(instrumentId);
    if (!prefix) return;

    const howl = this.getSpatialHowl(
      `${prefix}${noteId}`,
      worldResource(`/sfx/${prefix}${padWithZeros(noteId, 3)}.wav`),
    );
    if (!howl) {
      this.playNoteSfx(instrumentId, noteId);
      return;
    }
    try {
      const soundId = howl.play();
      howl.volume(this.client.configController.effectVolume, soundId);
      howl.pos(coords.x, 0, coords.y, soundId);
    } catch (error) {
      this.disableSpatialAudio(error);
      this.playNoteSfx(instrumentId, noteId);
    }
  }

  /** Play a non-positional (global) sound effect. */
  playById(id: SfxId, volume = 1.0): void {
    const key = id.toString();
    try {
      let howl = this.sfxCache.get(key);
      if (!howl) {
        howl = new Howl({ src: [this.sfxUrl(id)] });
        this.sfxCache.set(key, howl);
      }
      const soundId = howl.play();
      howl.volume(volume * this.client.configController.effectVolume, soundId);
    } catch (error) {
      this.disableSpatialAudio(error);
      console.warn('Sound effect unavailable; continuing without audio.', error);
    }
  }

  /** Play a positional sound at the given map tile coordinates. */
  playAtPosition(id: SfxId, coords: Vector2): void {
    const howl = this.getSpatialHowl(`${id}`, this.sfxUrl(id));
    if (!howl) {
      this.playById(id);
      return;
    }
    try {
      const soundId = howl.play();
      howl.volume(this.client.configController.effectVolume, soundId);
      howl.pos(coords.x, 0, coords.y, soundId);
    } catch (error) {
      this.disableSpatialAudio(error);
      this.playById(id);
    }
  }

  /**
   * Update Howler's global listener position to the player's current tile.
   * Call this whenever the player moves or warps.
   */
  updateListenerPosition(coords: Vector2): void {
    try {
      if (!this.spatialAudioDisabled) Howler.pos(coords.x, 0, coords.y);
    } catch (error) {
      this.disableSpatialAudio(error);
    }
  }

  stopAmbientSound(): void {
    const howl = this.ambientHowl;
    this.ambientHowl = null;
    if (!howl) return;

    // Ambient audio is optional. Browser audio-context resets must never make
    // leaving a map (or loading the next one) fail.
    try { howl.stop(); }
    catch (error) { console.warn('Unable to stop ambient audio cleanly', error); }
    try { howl.unload(); }
    catch (error) { console.warn('Unable to unload ambient audio cleanly', error); }
  }

  startAmbientSound(): void {
    if (!this.client.map?.ambientSoundId) {
      return;
    }

    const playerAt = this.client.getPlayerCoords();
    const source = this.getNearestAmbientSource(playerAt);

    const src = this.sfxUrl(this.client.map.ambientSoundId as SfxId);
    const options = {
      src: [src],
      loop: true,
      volume: this.client.configController.ambientVolume,
    };
    let howl: Howl | null = null;
    const disposeFailedHowl = () => {
      if (!howl) return;
      try { howl.stop(); } catch { /* failed graph may already be detached */ }
      try { howl.unload(); } catch { /* failed graph may already be detached */ }
      if (this.ambientHowl === howl) this.ambientHowl = null;
    };

    try {
      howl = new Howl(options);
      this.ambientHowl = howl;
      const spatialAudio = Boolean(source) && !this.spatialAudioDisabled;
      if (spatialAudio) howl.pannerAttr(SPATIAL_PANNER);
      const soundId = howl.play();
      if (spatialAudio && source) howl.pos(source.x, 0, source.y, soundId);
    } catch (error) {
      this.disableSpatialAudio(error);
      console.warn('Ambient audio setup failed; retrying without spatial audio.', error);
      disposeFailedHowl();
      try {
        howl = new Howl(options);
        this.ambientHowl = howl;
        howl.play();
      } catch (fallbackError) {
        this.disableSpatialAudio(fallbackError);
        console.warn('Ambient audio unavailable; map load will continue.', fallbackError);
        disposeFailedHowl();
      }
    }
  }

  /** Lazily initializes the MIDI synthesizer on first use. */
  private async ensureSynth(): Promise<void> {
    if (this.synthInitPromise) {
      return this.synthInitPromise;
    }

    this.synthInitPromise = (async () => {
      try {
        // Reuse the AudioContext created synchronously in notifyGesture() if available.
        if (!this.midiContext) {
          this.midiContext = new AudioContext();
        }

        if (!this.midiContext.audioWorklet) {
          console.warn('MIDI disabled: AudioWorklet not supported');
          return;
        }

        // iOS Safari requires the context to be running before addModule works.
        if (this.midiContext.state === 'suspended') {
          await this.midiContext.resume();
        }

        await this.midiContext.audioWorklet.addModule(processorUrl);

        this.midiSynth = new WorkletSynthesizer(this.midiContext);
        this.midiSynth.connect(this.midiContext.destination);

        if (!this.sfBuffer) {
          if (!this.client.config.soundFont) {
            console.warn('No sound font specified in config; MIDI disabled');
            return;
          }
          const res = await fetch(worldResource(`/${this.client.config.soundFont}`));
          this.sfBuffer = await res.arrayBuffer();
        }
        await this.midiSynth.soundBankManager.addSoundBank(this.sfBuffer, 'gm');
        await this.midiSynth.isReady;

        this.midiSequencer = new Sequencer(this.midiSynth);

        this.midiSequencer.eventHandler.addEvent(
          'songEnded',
          'audio-controller',
          () => {
            if (this.pendingMusic) {
              const { mfxId, loop } = this.pendingMusic;
              this.currentMfxId = null;
              this.pendingMusic = null;
              this.playMusic(mfxId, loop);
            } else if (this.jukeboxActive) {
              this.jukeboxActive = false;
              this.currentMfxId = null;
              if (this.mapMusicState) {
                this.playMusic(
                  this.mapMusicState.mfxId,
                  this.mapMusicState.loop,
                );
              }
            } else if (this.alwaysLoop && this.currentMfxId !== null) {
              this.playMusic(this.currentMfxId, true);
            }
          },
        );

        this.applyMusicVolume();
      } catch (e) {
        console.warn('MIDI init failed:', e);
        this.synthInitPromise = null;
      }
    })();

    return this.synthInitPromise;
  }

  /** Play a MIDI file by its mfx ID. Pass `loop = true` for repeating music. */
  async playMusic(
    mfxId: number,
    loop: boolean,
    path: 'mfx' | 'jbox' = 'mfx',
  ): Promise<void> {
    // Replace legacy music, not sound effects. HTML media streaming avoids both
    // the MIDI download delay and the cross-AudioContext spatial graph bug.
    await this.playOriginal(originalMusicTrack(mfxId, path === 'jbox'), loop);
    return;
  }

  private async playOriginal(track: string, loop: boolean): Promise<void> {
    this.originalTrack = track;
    this.applyMusicVolume();
    await this.originalMusic.play(`/soundtrack/${track}`, loop,
      this.gestureUnlocked && this.client.configController.musicVolume > 0
    ).catch(error => console.warn('Music playback deferred; game loading continues.', error));
  }

  /** MIDI compatibility retained for deployments without the original recordings. */
  private async playLegacyMusic(mfxId: number, loop: boolean, path: 'mfx' | 'jbox' = 'mfx'): Promise<void> {
    if (this.client.configController.musicVolume <= 0) {
      return;
    }

    // Browser autoplay policy: AudioContext can't play until a user gesture.
    // Queue the request and wait for unlock via notifyGesture().
    if (!this.gestureUnlocked) {
      this.queuedMusic = { mfxId, loop };
      return;
    }

    await this.ensureSynth();

    if (!this.midiSequencer) {
      return; // AudioWorklet not supported on this device
    }

    const url = `/${path}/${path}${padWithZeros(mfxId, 3)}.mid`;
    const res = await fetch(worldResource(url));
    if (!res.ok) {
      return;
    }
    const buffer = await res.arrayBuffer();

    this.currentMfxId = mfxId;
    this.midiSequencer!.loadNewSongList([{ binary: buffer }]);
    this.midiSequencer!.loopCount = loop ? Number.POSITIVE_INFINITY : 0;
    this.midiSequencer!.play();
  }

  /**
   * Call this on the first meaningful user gesture (click / keydown / touchstart).
   * Resumes the AudioContext (created eagerly in tryAutoplay) and starts any
   * queued music.
   */
  notifyGesture(): void {
    // Retry media playback synchronously inside every genuine gesture, including
    // mobile browsers that rejected the previous asynchronous autoplay attempt.
    if (this.originalAudio?.paused && this.client.configController.musicVolume > 0) {
      void this.originalAudio.play().catch(() => { /* autoplay policy; next gesture retries */ });
    }
    if (this.gestureUnlocked) {
      return;
    }
    this.gestureUnlocked = true;

    if (!this.midiContext) {
      try {
        this.midiContext = new AudioContext();
      } catch {
        return;
      }
    }

    // Resume then play — ensureSynth also resumes, but doing it here first
    // ensures the context is running before any async chain starts.
    const resume =
      this.midiContext.state === 'suspended'
        ? this.midiContext.resume()
        : Promise.resolve();

    if (this.queuedMusic) {
      const { mfxId, loop } = this.queuedMusic;
      this.queuedMusic = null;
      resume.then(() => this.playMusic(mfxId, loop));
    }
  }

  /** Stop all MIDI music immediately and clear any pending music. */
  stopMusic(): void {
    ++this.musicRequest;
    this.originalMusic.stop();
    this.originalTrack = null;
    this.queuedMusic = null;
    if (this.midiSequencer) {
      this.midiSequencer.pause();
    }
    this.currentMfxId = null;
    this.pendingMusic = null;
    this.jukeboxActive = false;
  }

  /**
   * Play a jukebox track once. When finished, automatically resumes the
   * current map's music (if any). A map change clears this behaviour.
   */
  async playJukeboxMusic(trackId: number): Promise<void> {
    this.jukeboxActive = true;
    this.pendingMusic = null;
    await this.playMusic(trackId, false, 'jbox');
  }

  /**
   * Used when the server directly commands a music change (e.g. via a map effect).
   */
  async playServerMusic(mfxId: number): Promise<void> {
    this.jukeboxActive = true;
    this.pendingMusic = null;
    await this.playMusic(mfxId, false);
  }

  /**
   * Handle map music changes according to the map's `musicControl` field.
   * Call this whenever a new map is loaded.
   */
  async handleMapMusic(
    musicId: number,
    control: MapMusicControl,
  ): Promise<void> {
    if (this.client.state !== GameState.InGame) {
      return;
    }
    void refreshOriginalSoundtrack();
    await this.playOriginal(originalMapTrack(this.client.mapId, this.client.map?.name ?? ''), true);
    return;


    this.jukeboxActive = false;

    if (control === MapMusicControl.InterruptPlayNothing || musicId === 0) {
      this.mapMusicState = null;
      this.stopMusic();
      return;
    }

    const loop =
      control === MapMusicControl.InterruptIfDifferentPlayRepeat ||
      control === MapMusicControl.InterruptPlayRepeat ||
      control === MapMusicControl.FinishPlayRepeat;

    this.mapMusicState = { mfxId: musicId, loop };

    switch (control) {
      case MapMusicControl.InterruptIfDifferentPlayOnce:
      case MapMusicControl.InterruptIfDifferentPlayRepeat:
        if (this.currentMfxId === musicId) {
          return;
        }
        await this.playMusic(musicId, loop);
        break;

      case MapMusicControl.InterruptPlayOnce:
      case MapMusicControl.InterruptPlayRepeat:
        await this.playMusic(musicId, loop);
        break;

      case MapMusicControl.FinishPlayOnce:
      case MapMusicControl.FinishPlayRepeat:
        if (this.currentMfxId === null) {
          await this.playMusic(musicId, loop);
        } else {
          this.pendingMusic = { mfxId: musicId, loop };
        }
        break;
    }
  }

  /**
   * Handle game state changes to control title screen music.
   * Title music (mfx001) plays on all non-InGame screens.
   */
  handleStateChange(state: GameState): void {
    if (state === GameState.InGame) {
      // Map music will be triggered separately via handleMapMusic in setMap().
      this.stopMusic();
    } else if (
      this.originalTrack !== originalMenuTrack &&
      this.client.configController.musicVolume > 0
    ) {
      this.playMusic(1, true);
    }
  }
}
